/**
 * Filtre du bruit tiers avant envoi à Sentry.
 *
 * ── Le problème ──────────────────────────────────────────────────────────────
 * Le 05/08/2026, sur 25 issues non résolues du projet, onze ne venaient d'aucun
 * code que nous maîtrisons :
 *   · 10 issues d'une même extension Chrome (`chrome-extension://hoklmmgf…`,
 *     BIZCOACH-15 à 1C et W) — « Failed to fetch », levé dans le code de
 *     l'extension d'un visiteur ;
 *   · un `fetch` tapé dans la console DevTools d'une review app, dont le body
 *     était lu deux fois (BIZCOACH-1Q, frame `<anonymous>:1:21`).
 *
 * Aucune n'est corrigeable : le code fautif ne nous appartient pas, et nous
 * n'avons pas choisi qu'il s'exécute. Mais elles remplissent la liste et noient
 * les vraies — celles qui portent une frame `src/…` et méritent un regard.
 *
 * Le widget Zoho SalesIQ (BIZCOACH-P) est un cas à part et reste SURVEILLÉ :
 * c'est un outil que KESTIO a choisi et opère — s'il casse, nos utilisateurs
 * n'ont plus de support. Cf. `HOTES_SURVEILLES`.
 *
 * ── La règle ─────────────────────────────────────────────────────────────────
 * On écarte un événement quand **aucune** de ses frames n'est à nous. Une seule
 * frame de notre code suffit à le garder : un tiers qui casse NOTRE rendu reste
 * notre problème (cf. `dom-guards.ts`, où Google Translate cassait React —
 * l'erreur portait bien une frame React et devait remonter).
 *
 * Deux prudences délibérées :
 *   · un événement **sans aucune frame** est CONSERVÉ. Beaucoup d'erreurs
 *     légitimes n'ont pas de stacktrace exploitable (erreurs réseau, promesses
 *     rejetées avec une valeur non-Error) ; les jeter en masse reviendrait à se
 *     rendre aveugle pour faire de la place.
 *   · nos chunks (`/chunk-xxx.js`, servis depuis notre origine) sont à NOUS.
 *     « error loading dynamically imported module » est un vrai symptôme —
 *     l'utilisateur avait la page ouverte pendant un déploiement.
 */

/** Frame Sentry, réduite à ce qui nous sert ici. */
export interface FrameLike {
  filename?: string | null;
  abs_path?: string | null;
}

/** Événement Sentry, réduit à ce qui nous sert ici. */
export interface EventLike {
  exception?: {
    values?: Array<{ stacktrace?: { frames?: FrameLike[] | null } | null }> | null;
  } | null;
}

/**
 * Origines qui ne sont jamais les nôtres, quel que soit l'environnement.
 *
 * `<anonymous>` couvre le code évalué : snippet de console, `eval`, `new
 * Function`. C'est ce qui a produit BIZCOACH-1Q.
 */
const PREFIXES_TIERS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "ms-browser-extension://",
];

/**
 * Tiers que nous avons CHOISI d'embarquer, et dont les erreurs nous concernent.
 *
 * Le widget de chat Zoho SalesIQ est un outil que KESTIO met en place et opère :
 * s'il casse, ce sont nos utilisateurs qui n'ont plus de support, et c'est à nous
 * de le voir (décision de Valérie, 05/08/2026). Ses frames sont donc traitées
 * comme les nôtres, alors même qu'elles viennent d'un autre domaine.
 *
 * Rien à voir avec une extension installée par un visiteur, que nous ne
 * choisissons pas et sur laquelle nous ne pouvons rien.
 */
const HOTES_SURVEILLES = [
  "salesiq.zohopublic.com",
  "zoho-salesiq-widgets",
  "salesiq.zoho.com",
  "js.zohostatic.com",
];

/**
 * Cette frame vient-elle d'ailleurs que de notre application ?
 *
 * `origine` = l'origine de la page (`location.origin`). Tout ce qui en vient est
 * à nous — y compris les chunks du bundler. Une frame sans fichier, ou nommée
 * `<anonymous>`, n'est rattachable à rien : elle ne nous appartient pas.
 */
export function estFrameTierce(frame: FrameLike, origine: string): boolean {
  const chemin = (frame.filename || frame.abs_path || "").trim();

  // Les tiers que nous opérons passent AVANT toute autre règle : leur domaine
  // n'est pas le nôtre, ils seraient écartés par le test d'origine plus bas.
  if (HOTES_SURVEILLES.some((h) => chemin.includes(h))) return false;

  if (!chemin) return true;
  if (chemin === "<anonymous>" || chemin.startsWith("<anonymous>")) return true;
  if (PREFIXES_TIERS.some((p) => chemin.startsWith(p))) return true;

  // Chemin relatif (`src/pages/TrainingPage`, `app:///chunk.js`) → à nous.
  if (!/^https?:\/\//i.test(chemin)) return false;

  // Absolu : à nous seulement s'il part de l'origine de la page.
  return origine ? !chemin.startsWith(origine) : false;
}

/**
 * L'événement est-il du bruit tiers, sans aucune frame de notre code ?
 *
 * Retourne `false` (= on garde) dès qu'un doute existe : pas de frame du tout,
 * structure inattendue, origine inconnue.
 */
export function estBruitTiers(event: EventLike, origine: string): boolean {
  const valeurs = event?.exception?.values;
  if (!Array.isArray(valeurs) || valeurs.length === 0) return false;

  const frames = valeurs.flatMap((v) => (Array.isArray(v?.stacktrace?.frames) ? v.stacktrace!.frames! : []));
  if (frames.length === 0) return false;

  return frames.every((f) => estFrameTierce(f, origine));
}
